import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth, initAuth } from './shared/auth/AuthContext';
import { setTokenGetter } from './shared/api/client';
import { ToastContainer } from './shared/components/ui/Toast';
import { toast } from './shared/hooks/useToast';
import { AppRouter } from './app/Router';
import './app/theme.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  // Surface ALL query errors as toasts — no more silent failures
  // (errors still go into query.error too, for per-page error banners)
});

// Global error handler — catches every query/mutation failure
queryClient.getQueryCache().subscribe(event => {
  if (event.type === 'updated' && event.action.type === 'error') {
    const err = event.action.error;
    const msg = err instanceof Error ? err.message : String(err);
    // Avoid double-toasting mutation errors (mutations handle their own)
    const key = JSON.stringify(event.query.queryKey);
    console.error('[query error]', key, msg);
    toast(`API Error: ${msg}`, 'error');
  }
});

function TokenWirer() {
  const { getToken } = useAuth();
  setTokenGetter(getToken);
  return null;
}

const rootEl = document.getElementById('root')!;

initAuth().then(({ user, token }) => {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider initialUser={user} initialToken={token}>
          <TokenWirer />
          <AppRouter />
          <ToastContainer />
        </AuthProvider>
      </QueryClientProvider>
    </StrictMode>
  );
});
